import RequestService from "./RequestService";

// --- Raw API response types ---

export interface AgentMonitorSummaryResponse {
  availableAgents: number;
  unassignedChats: number;
  totalOpenChats: number;
  chatsEnded: number;
}

interface AgentMonitorAgentRaw {
  userId: number;
  firstName: string;
  lastName: string;
  totalOpenChats: number;
  chatsEnded: number;
  isAvailable: boolean;
  statusChangedAt?: string;
  connectedHours?: string;
}

interface AgentMonitorAgentsRawResponse {
  list: AgentMonitorAgentRaw[];
}

interface ChatRaw {
  messageId: number;
  personId: number;
  personName: string;
  lastContent: string;
  lastDate: string;
}

interface OpenChatsRawResponse {
  chats: ChatRaw[];
}

// --- UI types ---

export interface AgentMonitorAgent {
  agentId: number | null;
  firstName: string;
  lastName: string;
  agentName: string;
  agentInitials: string;
  available: boolean;
  timeInStatus?: string;
  openChats: number;
  chatsEnded: number;
}

export interface AgentConversation {
  conversationId: number;
  personId: number;
  clientName: string;
  lastMessage: string;
  lastMessageTime: string;
}

export interface ReassignRequest {
  FromAgentId: number | null;
  ToAgentId: number | null;
  MessageIds: number[];
}

// --- Mapping helpers ---

function formatTimeInStatus(dateStr?: string): string | undefined {
  if (!dateStr) return undefined;
  try {
    const diffMs = Date.now() - new Date(dateStr).getTime();
    if (isNaN(diffMs) || diffMs < 0) return undefined;
    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  } catch {
    return undefined;
  }
}

function mapAgent(raw: AgentMonitorAgentRaw): AgentMonitorAgent {
  const agentName = `${raw.lastName}, ${raw.firstName}`;
  const agentInitials =
    `${raw.firstName.charAt(0)}${raw.lastName.charAt(0)}`.toUpperCase();
  return {
    agentId: raw.userId,
    firstName: raw.firstName,
    lastName: raw.lastName,
    agentName,
    agentInitials,
    available: raw.isAvailable,
    timeInStatus: raw.connectedHours || formatTimeInStatus(raw.statusChangedAt),
    openChats: raw.totalOpenChats,
    chatsEnded: raw.chatsEnded
  };
}

function formatChatTime(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return dateStr;
  }
}

// --- Service ---

class AgentMonitorService {
  public static getSummary(): Promise<AgentMonitorSummaryResponse | Error> {
    return RequestService.get("/agentmonitoring/summary", {});
  }

  public static async getAgents(): Promise<AgentMonitorAgent[] | Error> {
    const result = await RequestService.get<AgentMonitorAgentsRawResponse>(
      "/agentmonitoring/agents",
      {}
    );
    if (result instanceof Error) return result;
    return result.list.map(mapAgent);
  }

  public static async getOpenChats(
    agentId: number | null
  ): Promise<AgentConversation[] | Error> {
    const result = await RequestService.get<OpenChatsRawResponse>(
      `/agentmonitoring/openchats/${agentId ?? 0}`,
      {}
    );
    if (result instanceof Error) return result;
    return result.chats.map((chat) => ({
      conversationId: chat.messageId,
      personId: chat.personId,
      clientName: chat.personName,
      lastMessage: chat.lastContent,
      lastMessageTime: formatChatTime(chat.lastDate)
    }));
  }

  public static reassign(request: ReassignRequest): Promise<any | Error> {
    return RequestService.post("/agentmonitoring/assignagent", { ...request });
  }
}

export default AgentMonitorService;
